/**
 * HistoryPage — Training Portal agent session history & Certification Wallet.
 */

import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../hooks/useAuth';

import { ScoreHistoryChart, ScoreTrendPoint } from '../../../shared/components/ScoreHistoryChart';
import { SessionHistoryTable, SessionRow } from '../../../shared/components/SessionHistoryTable';
import styles from './HistoryPage.module.css';

const PAGE_SIZE = 10;

interface Certificate {
  id: string;
  courseTitle: string;
  score: number;
  issueDate: string;
  expiryDate: string;
  status: string;
}

export function HistoryPage(): JSX.Element {
  const { t } = useTranslation('training');
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState<'wallet' | 'history'>('wallet');

  // Original state
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [scoreTrend, setScoreTrend] = useState<ScoreTrendPoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  // Certificate Modal state
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // Original history fetch logic
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    axios
      .get(`/api/v1/agents/me/history?page=${page}&limit=${PAGE_SIZE}&days=90`)
      .then((res) => {
        if (cancelled) return;
        const data = res.data.data;
        setSessions(data.sessions);
        setTotal(data.total);
        if (page === 1) {
          setScoreTrend(data.score_trend);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('History fetch error', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [page]);

  // Mock certifications matching user completions or standard portfolio
  const certifications: Certificate[] = useMemo(() => [
    {
      id: 'cert-1',
      courseTitle: 'Claims Handling Essentials',
      score: 95,
      issueDate: '10 Feb 2026',
      expiryDate: '09 Feb 2027',
      status: 'Active',
    },
    {
      id: 'cert-2',
      courseTitle: 'Insurance Fundamentals',
      score: 88,
      issueDate: '15 Jan 2026',
      expiryDate: '14 Jan 2027',
      status: 'Active',
    },
    {
      id: 'cert-3',
      courseTitle: 'HIPAA Compliance 2026',
      score: 100,
      issueDate: '05 Mar 2026',
      expiryDate: '04 Mar 2027',
      status: 'Active',
    },
    {
      id: 'cert-4',
      courseTitle: 'Fraud Detection Specialist',
      score: 92,
      issueDate: '20 Apr 2026',
      expiryDate: '19 Apr 2027',
      status: 'Active',
    },
  ], []);

  const studentName = user?.full_name ?? 'Dimple Sharma';

  return (
    <div className={styles.page} data-testid="history-page">
      <h1 className={styles.title}>
        My Certifications &amp; History
      </h1>

      {/* Tabs */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tabBtn} ${activeTab === 'wallet' ? styles.tabBtnActive : ''}`}
          onClick={() => setActiveTab('wallet')}
        >
          Certification Wallet
        </button>
        <button
          className={`${styles.tabBtn} ${activeTab === 'history' ? styles.tabBtnActive : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Activity &amp; Session History
        </button>
      </div>

      {activeTab === 'wallet' && (
        <div className={styles.walletGrid}>
          {certifications.map((cert) => (
            <div key={cert.id} className={styles.certCard}>
              <div className={styles.badgeRow}>
                <span className={styles.goldSeal}>🏅</span>
                <span className={styles.statusActive}>{cert.status}</span>
              </div>
              <h3 className={styles.cardTitle}>{cert.courseTitle}</h3>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Passing Score:</span>
                <span className={styles.metaVal}>{cert.score}%</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Issued:</span>
                <span className={styles.metaVal}>{cert.issueDate}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Expires:</span>
                <span className={styles.metaVal}>{cert.expiryDate}</span>
              </div>
              <button
                type="button"
                className={styles.viewBtn}
                onClick={() => setSelectedCert(cert)}
              >
                View Certificate
              </button>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'history' && (
        <>
          {/* Score trend chart */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              padding: '1.25rem',
              marginBottom: '1.5rem',
            }}
            data-testid="score-chart-panel"
          >
            <ScoreHistoryChart
              data={scoreTrend}
              passThreshold={0.7}
              height={220}
              i18nNs="training"
            />
          </div>

          {/* Session history table */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e5e7eb',
              borderRadius: 10,
              padding: '1.25rem',
            }}
            data-testid="session-table-panel"
          >
            {loading ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                {t('common.loading')}
              </div>
            ) : (
              <SessionHistoryTable
                sessions={sessions}
                portal="training"
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                i18nNs="training"
                keyPrefix="history"
              />
            )}
          </div>
        </>
      )}

      {/* Certification Modal popup */}
      {selectedCert && (
        <div className={styles.modalOverlay} onClick={() => setSelectedCert(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.certFrame}>
              <div className={styles.certHeader}>
                <div className={styles.logoRow}>
                  <div className={styles.logoMark}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <rect x="3" y="3" width="7" height="7" rx="1.5" fill="white" />
                      <rect x="14" y="3" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
                      <rect x="3" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.7" />
                      <rect x="14" y="14" width="7" height="7" rx="1.5" fill="white" opacity="0.5" />
                    </svg>
                  </div>
                  <span className={styles.logoText}>ARIA</span>
                </div>
                <span className={styles.certBadgeTop}>OFFICIAL VERIFICATION</span>
              </div>

              <h2 className={styles.certMainTitle}>Certificate of Completion</h2>
              <div className={styles.certSubtitle}>SalesCoachAI Training Program</div>
              
              <p className={styles.certText}>This is proudly presented to</p>
              <div className={styles.certRecipientName}>{studentName}</div>
              
              <p className={styles.certText}>for successfully demonstrating competency in the course</p>
              <h3 className={styles.certCourseTitle}>{selectedCert.courseTitle}</h3>
              
              <div className={styles.certScoreRow}>
                Passing Score achieved: {selectedCert.score}%
              </div>

              <div className={styles.certFooter}>
                <div className={styles.certDateCol}>
                  <div className={styles.certDateLabel}>Issue Date</div>
                  <div className={styles.certDateVal}>{selectedCert.issueDate}</div>
                  <div className={styles.certDateLabel} style={{ marginTop: '0.5rem' }}>Expiry Date</div>
                  <div className={styles.certDateVal}>{selectedCert.expiryDate}</div>
                </div>

                <div className={styles.certSealCol}>
                  ★
                </div>

                <div className={styles.certSigCol}>
                  <span className={styles.certSigLine}>Marc de Groot</span>
                  <span className={styles.certSigLabel}>Chief Learning Officer, ARIA</span>
                </div>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.closeBtn}
                onClick={() => setSelectedCert(null)}
              >
                Close
              </button>
              <button
                type="button"
                className={styles.downloadBtn}
                onClick={() => alert('Certificate PDF download started! (stub)')}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
